import React from "react";
import moment from "moment";

import { getDurationMinutes } from "../flight-search/FlightResultCard";

export const PRIMARY = "var(--btn-bg)";
export const BOOKING_STORAGE_KEY = "flight-booking-draft";

export const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const formatTime = (t) => (t ? String(t).slice(0, 5) : "--:--");
const formatDuration = (mins) => (mins ? `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m` : "-");

export const PAX_TYPES = [
  { key: "adults", fareKey: "adult", label: "Adult", hint: "12 yrs+", titles: ["Mr", "Mrs", "Ms"] },
  { key: "children", fareKey: "child", label: "Child", hint: "2–12 Yrs", titles: ["Master", "Miss"] },
  { key: "infants", fareKey: "infant", label: "Infant", hint: "Below 2 Yrs", titles: ["Master", "Miss"] },
];

// The draft (fare, search, passengers) is kept in sessionStorage so a page refresh does not lose it
export const loadDraft = () => {
  try {
    return JSON.parse(sessionStorage.getItem(BOOKING_STORAGE_KEY));
  } catch (err) {
    return null;
  }
};

export const saveDraft = (draft) => {
  try {
    sessionStorage.setItem(BOOKING_STORAGE_KEY, JSON.stringify(draft));
  } catch (err) {
    // storage unavailable: router state still carries the draft
  }
};

export const getRouteType = (fare, search) => (search?.route_type || fare?.route_type || "domestic").toUpperCase();

// Step indicator: step 1 = Traveller details, step 2 = Confirm & pay (flight selection is always done)
export const StepBar = ({ step }) => (
  <div className="fb-steps">
    <span className="fb-step done"><b><i className="bi bi-check-lg"></i></b> Flight selected</span>
    <span className="fb-step-line" />
    <span className={`fb-step ${step == 1 ? "active" : "done"}`}>
      {step == 1 ? <b>2</b> : <b><i className="bi bi-check-lg"></i></b>} Traveller details
    </span>
    <span className="fb-step-line" />
    <span className={`fb-step ${step == 2 ? "active" : ""}`}><b>3</b> Confirm &amp; pay</span>
  </div>
);

// Page header: back arrow, title, route summary and the step bar
export const BookingHeader = ({ fare, pax, step, onBack }) => {
  const travellers = (pax?.adults || 0) + (pax?.children || 0) + (pax?.infants || 0);
  return (
    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
      <div className="d-flex align-items-center gap-3">
        <button type="button" className="fb-back-btn" onClick={onBack} aria-label="Go back">
          <i className="bi bi-arrow-left"></i>
        </button>
        <div>
          <div className="fb-title" style={{ fontSize: 24 }}>Flight Booking</div>
          <div className="fb-muted">
            {fare.origin?.city_name} → {fare.destination?.city_name} · {travellers} traveller{travellers == 1 ? "" : "s"}
          </div>
        </div>
      </div>
      <StepBar step={step} />
    </div>
  );
};

export const FlightHeader = ({ fare, search }) => {
  const duration = getDurationMinutes(fare);
  const stops = Number(fare.flight_stops || 0);
  const airline = (fare.airline?.airline_name || "").toUpperCase();
  const meta = [moment(fare.origin?.date).format("D MMM YYYY"), airline, `${formatTime(fare.origin?.time)}-${formatTime(fare.destination?.time)}`, fare.cabin_class, stops == 0 ? "Non-stop" : `${stops} Stop${stops > 1 ? "s" : ""}`].filter(Boolean).join(" • ");
  return (
    <div className="fb-flight2">
      <div className="fb-flight2-head">
        <span className="fb-flight2-route">{fare.origin?.city_name} → {fare.destination?.city_name}</span>
        <span className="fb-muted text-capitalize">{meta}</span>
      </div>
      <div className="fb-flight2-body">
        <div className="d-flex align-items-center gap-2">
          {fare.airline?.logo ? (
            <img src={fare.airline.logo} alt={fare.airline?.airline_code} className="fb-logo" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <div className="fb-logo fb-logo-fallback">{fare.airline?.airline_code}</div>
          )}
          <div>
            <div className="fb-strong">{airline}</div>
            <div className="fb-muted">{fare.flight_number}</div>
          </div>
        </div>
        <div>
          <div className="fb-time">{formatTime(fare.origin?.time)}</div>
          <div className="fb-strong" style={{ fontWeight: 500 }}>{fare.origin?.city_name} ({fare.origin?.airport_code})</div>
          <div className="fb-muted">{moment(fare.origin?.date).format("D MMM YYYY")}</div>
          <div className="fb-muted fb-small">{fare.origin?.airport_name}</div>
        </div>
        <div className="fb-path">
          <div className="fb-muted">{formatDuration(duration)}</div>
          <div className="fb-line2"><i className="bi bi-airplane-fill"></i></div>
          <div className="fb-nonstop">{stops == 0 ? "Nonstop" : `${stops} Stop${stops > 1 ? "s" : ""}`}</div>
        </div>
        <div className="text-end">
          <div className="fb-time">{formatTime(fare.destination?.time)}</div>
          <div className="fb-strong" style={{ fontWeight: 500 }}>{fare.destination?.city_name} ({fare.destination?.airport_code})</div>
          <div className="fb-muted">{moment(fare.destination?.date || fare.origin?.date).format("D MMM YYYY")}</div>
          <div className="fb-muted fb-small">{fare.destination?.airport_name}</div>
        </div>
      </div>
    </div>
  );
};

// Card whose body is hidden until the header is clicked
const CollapsibleCard = ({ icon, title, children }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="fb-card p-0">
      <button type="button" className="fb-card-title fb-card-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
        <i className={`bi ${icon}`}></i> {title}
        <i className={`bi bi-chevron-${open ? "up" : "down"} ms-auto`} style={{ color: "#64748b", fontSize: 13 }}></i>
      </button>
      {open && <div className="fb-card-body">{children}</div>}
    </div>
  );
};

// Baggage allowance + booking reference cards shown under the flight summary (collapsed by default)
export const BookingInfoCards = ({ fare }) => {
  const rows = [
    ["Flight Number", fare.flight_number],
    ["Airline Code", fare.airline?.airline_code],
    ["Route", `${fare.origin?.airport_code} → ${fare.destination?.airport_code}`],
    ["Onward Date", moment(fare.origin?.date).format("D MMM YYYY")],
    ["Arrival Date", moment(fare.destination?.date || fare.origin?.date).format("D MMM YYYY")],
  ];
  const checkin = fare.baggage_policy ? `${fare.baggage_policy} kg` : "-";
  const baggage = [
    ["bi-briefcase", "Cabin Baggage", "7 kg", "7 kg", "0 kg"],
    ["bi-bag-check", "Check-in Baggage", checkin, checkin, "-"],
  ];
  return (
    <div className="row g-3 mb-1 align-items-start">
      <div className="col-12 col-lg-7">
        <CollapsibleCard icon="bi-suitcase2" title="Baggage Allowance">
          <div className="fb-bag">
            {["Type", "Adult", "Child", "Infant"].map((h) => <div key={h} className="fb-bag-h">{h}</div>)}
            {baggage.map(([icon, label, ...vals]) => (
              <React.Fragment key={label}>
                <div className="fb-bag-label"><i className={`bi ${icon}`}></i> {label}</div>
                {vals.map((v, i) => <div key={i} className="fb-bag-v">{v}</div>)}
              </React.Fragment>
            ))}
          </div>
        </CollapsibleCard>
      </div>
      <div className="col-12 col-lg-5">
        <CollapsibleCard icon="bi-ticket-perforated" title="Booking Reference">
          {rows.map(([k, v]) => (
            <div className="fb-kv" key={k}><span className="fb-muted" style={{ fontSize: 13 }}>{k}</span><b>{v || "-"}</b></div>
          ))}
        </CollapsibleCard>
      </div>
    </div>
  );
};

export const BookingSummary = ({ fare, pax, children }) => {
  const fareRows = PAX_TYPES
    .filter((t) => pax[t.key] > 0)
    .map((t) => {
      const unit = fare.fare?.[t.fareKey]?.total_fare || 0;
      return { ...t, count: pax[t.key], unit, total: unit * pax[t.key] };
    });
  const estimatedTotal = fare?.fare?.total?.total_fare;
  return (
    <>
      <div className="fb-card fb-summary">
        <div className="fb-card-title"><i className="bi bi-receipt"></i> Fare Breakdown</div>

        <div className="fb-summary-rows">
          {fareRows.map((r) => (
            <React.Fragment key={r.key}>
              <div className="fb-kv">
                <span className="fb-muted-row">{r.label} Fare (×{r.count})</span>
                <b>{formatINR(r.total)}</b>
              </div>
              {/* <div className="fb-kv fb-kv-sub">
                <span>{r.label} Fare (per {r.label.toLowerCase()})</span>
                <span>{formatINR(r.unit)}</span>
              </div> */}
            </React.Fragment>
          ))}
        </div>

        <div className="fb-total">
          <span>Total Payable</span>
          <b style={{ color: PRIMARY }}>{formatINR(estimatedTotal)}</b>
        </div>
        {children}
      </div>
      <div className="fb-summary-note"><i className="bi bi-shield-check"></i> You can review everything before you pay</div>
    </>
  );
};

export const bookingCss = `
.fb-page { background:#f4f6fb; font-family:Inter, system-ui, sans-serif; }
.fb-back { border:none; background:none; color:#475569; font-size:13px; font-weight:500; padding:0; }
.fb-back:hover { color:${PRIMARY}; }
.fb-steps { display:flex; align-items:center; gap:10px; font-size:13px; color:#64748b; padding:8px 16px; background:#fff; border:1px solid #d5dbe8; border-radius:999px; flex-wrap:wrap; }
.fb-step-line { width:28px; height:1px; background:#cbd5e1; }
.fb-back-btn { width:40px; height:40px; border-radius:50%; border:1px solid #d5dbe8; background:#fff; color:#0f172a; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.fb-back-btn:hover { color:${PRIMARY}; border-color:${PRIMARY}; }
.fb-section-head { display:flex; align-items:center; gap:8px; font-size:15px; font-weight:700; color:#0f172a; margin:16px 0 10px; }
.fb-section-head i, .fb-card-title i { color:${PRIMARY}; }
.fb-card-title { display:flex; align-items:center; gap:8px; padding:12px 16px; font-size:15px; font-weight:700; color:#0f172a; border-bottom:1px solid #eef0f5; }
.fb-card-toggle { width:100%; background:none; border:none; text-align:left; }
.fb-card-toggle[aria-expanded="false"] { border-bottom:none; }
.fb-bag { display:grid; grid-template-columns:max-content repeat(3, minmax(48px,1fr)); column-gap:12px; font-size:13px; }
.fb-bag > div { padding:9px 0; border-bottom:1px solid #eef0f5; }
.fb-bag > div:nth-last-child(-n+4) { border-bottom:none; }
.fb-bag-h { color:#64748b; font-weight:500; text-align:right; }
.fb-bag-h:first-child, .fb-bag-label { text-align:left; }
.fb-bag-label { color:#475569; white-space:nowrap; }
.fb-bag-label i { margin-right:6px; }
.fb-bag-v { text-align:right; font-weight:600; color:#0f172a; }
.fb-card-body { padding:6px 18px 12px; }
.fb-flight2 { background:#fff; border:1px solid #d5dbe8; border-radius:14px; box-shadow:0 1px 2px rgba(16,24,40,.05); margin-bottom:12px; overflow:hidden; }
.fb-flight2-head { display:flex; align-items:baseline; gap:10px; flex-wrap:wrap; padding:10px 16px; border-bottom:1px solid #eef0f5; }
.fb-flight2-route { font-size:16px; font-weight:700; color:#0f172a; }
.fb-flight2-body { display:grid; grid-template-columns:minmax(110px,.8fr) 1.3fr 1.2fr 1.3fr; align-items:center; gap:12px; padding:12px 16px; }
.fb-flight2 .fb-logo { width:30px; height:30px; border-radius:5px; }
.fb-flight2 .fb-time { font-size:22px; }
.fb-line2 { position:relative; border-top:1px solid #cbd5e1; margin:10px 6px; }
.fb-line2::before { content:""; position:absolute; left:-4px; top:-4px; width:6px; height:6px; border:1px solid #94a3b8; border-radius:50%; background:#fff; }
.fb-line2 i { position:absolute; right:-6px; top:-8px; background:#fff; padding-left:4px; color:var(--btn-bg); font-size:14px; }
.fb-nonstop { font-size:12px; font-weight:600; color:#15803d; }
.fb-small { font-size:11px; }
.fb-goback { display:block; width:calc(100% - 32px); margin:10px 16px 0; background:none; border:none; color:#475569; font-size:13px; font-weight:500; }
.fb-goback:hover { color:${PRIMARY}; }
@media (max-width: 767px) { .fb-flight2-body { grid-template-columns:1fr 1fr; } .fb-flight2-body > :nth-child(3) { grid-column:1 / -1; order:5; } }
.fb-step { display:flex; align-items:center; gap:6px; font-weight:500; }
.fb-step b { width:20px; height:20px; border-radius:50%; background:#e5e7eb; color:#475569; font-size:11px; display:flex; align-items:center; justify-content:center; }
.fb-step.active { color:${PRIMARY}; font-weight:600; }
.fb-step.active b, .fb-step.done b { background:${PRIMARY}; color:#fff; }
.fb-card { background:#fff; border:1px solid #d5dbe8; border-radius:10px; padding:16px 18px; margin-bottom:12px; box-shadow:0 1px 2px rgba(16,24,40,.05); }
.fb-title { font-size:17px; font-weight:700; color:#0f172a; margin-bottom:2px; }
.fb-strong { font-size:14px; font-weight:600; color:#0f172a; }
.fb-muted { font-size:12px; color:#64748b; }
.fb-label { font-size:12px; font-weight:500; color:#475569; margin-bottom:4px; display:block; }
.fb-pax-grid { display:grid; grid-template-columns:96px minmax(0,1.3fr) minmax(0,1fr) minmax(0,1.2fr) minmax(0,1.2fr); gap:12px; align-items:start; }
.fb-cell { min-width:0; }
@media (max-width: 991px) { .fb-pax-grid { grid-template-columns:1fr 1fr; } }
@media (max-width: 575px) { .fb-pax-grid { grid-template-columns:1fr; } }
.fb-input { font-size:14px; height:40px; border-radius:8px; }
.fb-avatar { width:32px; height:32px; border-radius:8px; background:#e6f0ff; color:${PRIMARY}; display:flex; align-items:center; justify-content:center; }
.fb-avatar-muted { background:#f1f5f9; color:#64748b; }
.fb-check { font-size:13px; color:#0f172a; cursor:pointer; white-space:nowrap; }
.fb-review-row { display:flex; justify-content:space-between; gap:12px; padding:10px 0; border-top:1px solid #eef0f5; }

.fb-flight { display:grid; grid-template-columns:1.1fr 2.2fr 1fr; align-items:center; gap:0; padding:14px 6px; }
.fb-flight > div { padding:0 18px; border-left:1px solid #eceff4; min-width:0; }
.fb-flight > div:first-child { border-left:none; }
.fb-codes { font-size:20px; font-weight:700; color:#0f172a; }
.fb-codes i { font-size:13px; color:#64748b; }
.fb-logo { width:34px; height:34px; border-radius:6px; object-fit:contain; background:#f8f9fb; }
.fb-logo-fallback { display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; color:var(--btn-hover-bg); }
.fb-timeline { display:grid; grid-template-columns:auto 1fr auto; gap:14px; align-items:center; }
.fb-time { font-size:18px; font-weight:700; color:#0f172a; line-height:1.2; }
.fb-path { text-align:center; }
.fb-line { position:relative; border-top:1px dashed #cbd5e1; margin:6px 0; height:0; }
.fb-line i { position:absolute; left:50%; top:-8px; transform:translateX(-50%); background:#fff; padding:0 4px; color:var(--btn-bg); font-size:12px; }
.fb-flight-side { text-align:right; }
.fb-tag { display:inline-block; font-size:10px; font-weight:700; letter-spacing:.6px; padding:2px 8px; border:1px solid #d7dbe6; border-radius:5px; color:#475569; }
.fb-avail { display:inline-block; font-size:11px; font-weight:600; padding:1px 10px; border-radius:999px; background:#ecfdf3; color:#15803d; margin-top:4px; }
.fb-avail.low { background:#fff4e5; color:#c2410c; }

.fb-summary { padding:0 0 16px; border-radius:14px; }
.fb-summary > * { padding-left:16px; padding-right:16px; }
.fb-summary .fb-card-title { padding:12px 16px; }
.fb-summary-rows { padding-top:10px; padding-bottom:4px; }
.fb-muted-row { color:#475569; }
.fb-kv-sub { color:#94a3b8; font-size:12.5px; }
.fb-summary-note { text-align:center; font-size:12px; color:#64748b; margin-top:-2px; }
.fb-summary-note i { color:#16a34a; margin-right:6px; }
.fb-section { border-top:1px solid #eef0f5; margin-top:14px; padding-top:12px; }
.fb-section-title { font-size:11px; font-weight:700; letter-spacing:.7px; text-transform:uppercase; color:#64748b; margin-bottom:8px; }
.fb-kv { display:flex; justify-content:space-between; align-items:flex-start; font-size:13px; color:#334155; padding:2px 0; }
.fb-bar { height:4px; border-radius:999px; background:#e5e7eb; margin:8px 0 6px; overflow:hidden; padding:0 !important; }
.fb-bar span { display:block; height:100%; background:#22c55e; border-radius:999px; }
.fb-total { display:flex; justify-content:space-between; align-items:center; border-top:1px solid #eef0f5; margin:10px 16px 14px; padding:12px 0 0; font-weight:600; color:#0f172a; }
.fb-total b { font-size:22px; }
.fb-primary { width:calc(100% - 32px); margin:0 16px; padding:0; border:none; background:${PRIMARY}; color:#fff; border-radius:10px; height:44px; box-shadow:0 6px 16px rgba(15,118,110,.25); font-size:14px; font-weight:600; }
.fb-primary:hover { background:#01205a; }
.fb-primary:focus-visible { outline:2px solid ${PRIMARY}; outline-offset:2px; }

@media (max-width: 767px) {
  .fb-flight { grid-template-columns:1fr; padding:12px 0; }
  .fb-flight > div { border-left:none; border-top:1px solid #eceff4; padding:12px 16px 0; margin-top:12px; }
  .fb-flight > div:first-child { border-top:none; margin-top:0; padding-top:0; }
  .fb-flight-side { text-align:left; }
  .fb-check { white-space:normal; }
}
`;
